import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  GitBranch, Check, X, Sparkles, AlertTriangle, ExternalLink, ChevronDown, ChevronUp,
} from "lucide-react";

import { api, formatApiErrorDetail } from "../lib/api";

/**
 * Bannière de suggestions de lineage (50-85% confidence).
 * Affichée au-dessus de DocumentTimeline sur la page Analysis.
 * Les liens >= 85% sont déjà auto-créés par le backend (job_queue hook).
 */
export default function LineageSuggestionBanner({ docId, onLinked }) {
  const { t } = useTranslation("components");
  const [suggestions, setSuggestions] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [busyId, setBusyId] = useState(null);

  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/documents/${docId}/lineage/suggestions`);
      setSuggestions(r.data.suggestions || []);
      setMeta(r.data.lineage_meta || null);
    } catch (e) {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [docId]);

  useEffect(() => { fetchSuggestions(); }, [fetchSuggestions]);

  const confirm = async (candidate) => {
    setBusyId(candidate.candidate_doc_id);
    try {
      const r = await api.post(`/documents/${docId}/lineage/suggestions/confirm`, {
        candidate_doc_id: candidate.candidate_doc_id,
        relationship_kind: candidate.suggested_kind || "amendment",
      });
      const cs = r.data?.contradictions;
      if (cs?.created) {
        toast.success(t("lineage.confirmed_with_contradictions", {
          count: cs.created,
          defaultValue: `Lien confirmé — ${cs.created} contradictions détectées`,
        }));
      } else {
        toast.success(t("lineage.confirmed", { defaultValue: "Lien confirmé" }));
      }
      await fetchSuggestions();
      onLinked?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec");
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (candidate) => {
    setBusyId(candidate.candidate_doc_id);
    try {
      await api.post(`/documents/${docId}/lineage/suggestions/dismiss`, {
        candidate_doc_id: candidate.candidate_doc_id,
      });
      toast.success(t("lineage.dismissed", { defaultValue: "Suggestion rejetée" }));
      await fetchSuggestions();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec");
    } finally {
      setBusyId(null);
    }
  };

  // Show an "auto-linked" toast-style banner when status=modifier && parent_kind set,
  // but ONLY if we have no pending suggestions (otherwise the suggestions take priority).
  if (loading) return null;
  const autoLinked = meta && meta.status === "modifier" && meta.parent_doc_id;

  if (suggestions.length === 0 && !autoLinked) return null;

  return (
    <div
      className="rounded-xl border-2 border-violet-200 bg-gradient-to-br from-violet-50 to-violet-50/30 p-4 mb-5"
      data-testid="lineage-suggestion-banner"
    >
      <div className="flex items-start gap-3">
        <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-violet-100 text-violet-700">
          <GitBranch size={16} strokeWidth={1.75} />
        </span>
        <div className="flex-1 min-w-0">
          {autoLinked && suggestions.length === 0 && (
            <AutoLinkedRow meta={meta} t={t} />
          )}
          {suggestions.length > 0 && (
            <>
              <div className="flex items-baseline justify-between gap-2 mb-2">
                <p className="font-semibold text-sm text-zinc-950">
                  {t("lineage.suggestion_title", {
                    count: suggestions.length,
                    defaultValue: suggestions.length === 1
                      ? "Une suggestion de lien documentaire"
                      : `${suggestions.length} suggestions de lien documentaire`,
                  })}
                </p>
                <span className="text-[10px] uppercase tracking-wider text-violet-700 font-semibold">
                  {t("lineage.deterministic", { defaultValue: "Détection déterministe — $0 LLM" })}
                </span>
              </div>
              <p className="text-xs text-zinc-600 mb-3">
                {t("lineage.suggestion_body", {
                  defaultValue: "Le document semble lié à un document antérieur. Validez pour générer automatiquement les contradictions critiques.",
                })}
              </p>
              <ul className="space-y-2" data-testid="lineage-suggestions-list">
                {suggestions.map((s) => (
                  <SuggestionRow
                    key={s.candidate_doc_id}
                    suggestion={s}
                    expanded={!!expanded[s.candidate_doc_id]}
                    onToggle={() =>
                      setExpanded((v) => ({
                        ...v,
                        [s.candidate_doc_id]: !v[s.candidate_doc_id],
                      }))
                    }
                    onConfirm={() => confirm(s)}
                    onDismiss={() => dismiss(s)}
                    busy={busyId === s.candidate_doc_id}
                    t={t}
                  />
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AutoLinkedRow({ meta, t }) {
  return (
    <div className="flex items-center gap-2 text-xs" data-testid="lineage-auto-linked-banner">
      <Sparkles size={12} className="text-violet-600" />
      <span className="text-zinc-700">
        {t("lineage.auto_linked", {
          kind: meta.parent_kind || "amendment",
          defaultValue: `Détecté automatiquement comme ${meta.parent_kind || "modification"} d'un document antérieur.`,
        })}
      </span>
      {meta.parent_doc_id && (
        <Link
          to={`/analysis/${meta.parent_doc_id}`}
          className="inline-flex items-center gap-1 text-violet-800 hover:text-violet-950 font-medium"
          data-testid="lineage-view-parent"
        >
          {t("lineage.view_parent", { defaultValue: "Voir le document parent" })}
          <ExternalLink size={10} />
        </Link>
      )}
    </div>
  );
}

function SuggestionRow({ suggestion, expanded, onToggle, onConfirm, onDismiss, busy, t }) {
  const s = suggestion;
  return (
    <li
      className="bg-white border border-violet-100 rounded-lg p-3"
      data-testid={`lineage-suggestion-${s.candidate_doc_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-zinc-950 truncate" title={s.candidate_filename}>
            {s.candidate_filename || s.candidate_doc_id}
          </p>
          <div className="mt-1 flex items-center gap-2 text-[11px]">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 font-semibold">
              {t("lineage.confidence", {
                pct: s.score,
                defaultValue: `Confiance ${s.score}%`,
              })}
            </span>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 uppercase tracking-wider font-semibold">
              {s.suggested_kind || "amendment"}
            </span>
            {s.candidate_document_type && (
              <span className="text-zinc-500">{s.candidate_document_type}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-zinc-950 text-white text-[11px] font-medium hover:bg-zinc-800 disabled:opacity-60"
            data-testid={`lineage-suggestion-confirm-${s.candidate_doc_id}`}
          >
            <Check size={11} /> {t("lineage.confirm", { defaultValue: "Confirmer" })}
          </button>
          <button
            type="button"
            onClick={onDismiss}
            disabled={busy}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-zinc-300 text-[11px] text-zinc-600 hover:bg-zinc-50 disabled:opacity-60"
            data-testid={`lineage-suggestion-dismiss-${s.candidate_doc_id}`}
          >
            <X size={11} /> {t("lineage.dismiss", { defaultValue: "Rejeter" })}
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={onToggle}
        className="mt-2 inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-900"
        data-testid={`lineage-suggestion-toggle-evidence-${s.candidate_doc_id}`}
      >
        {expanded
          ? <>{t("lineage.hide_evidence", { defaultValue: "Masquer les preuves" })} <ChevronUp size={10} /></>
          : <>{t("lineage.show_evidence", { defaultValue: "Voir les preuves" })} <ChevronDown size={10} /></>}
      </button>
      {expanded && (
        <ul
          className="mt-2 space-y-1 text-[11px] text-zinc-600"
          data-testid={`lineage-suggestion-evidence-${s.candidate_doc_id}`}
        >
          {(s.evidence || []).map((ev, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <AlertTriangle size={10} className="text-zinc-400 mt-0.5 shrink-0" />
              <span>
                <strong className="text-zinc-800 uppercase tracking-wider">{ev.kind}</strong>
                {ev.shared && <>: {ev.shared.join(", ")} (jaccard {ev.jaccard})</>}
                {ev.matched_fields && <>: {ev.matched_fields.join(", ")}</>}
                {ev.suggested_kind && <>: → {ev.suggested_kind}</>}
                {ev.order && <>: {ev.order}</>}
                {ev.matched !== undefined && !ev.shared && <>: ✓</>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
